import { type ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { type BinaryFiles } from '@excalidraw/excalidraw/types/types';

import { type Page, PageTypes, type Position, type Presenter, type Session, type UserInfo } from '../types';
import { AT_LEAST_ONE_PAGE_REQUIRED, UNKNOWN_ACTION_ERROR } from '../whiteboard-errors';
import WhiteboardError from '../WhiteboardError';

export enum WhiteboardSessionActions {
    Updated = 'whiteboard-session:updated', // Websocket event.
    Deleted = 'whiteboard-session:deleted', // Websocket event.
    Present = 'whiteboard-session:present', // Websocket event.
    PresenterPosition = 'whiteboard-session:presenter-position', // Websocket event.
    Set = 'whiteboard-session:set'
}

export enum PageActions {
    Created = 'pages:created', // Websocket event.
    Updated = 'pages:updated', // Websocket event.
    Deleted = 'pages:deleted', // Websocket event.
    SetCurrentPage = 'page:set-current-page',
    SetPages = 'page:set-all',
    AcknowledgeNotFound = 'acknowledge-not-found'
}

export enum ElementActions {
    Created = 'local_element:created',
    Updated = 'local_element:updated',
    Deleted = 'local_element:deleted',
    Clear = 'local_elements:clear',
    RemoteCreated = 'element:created', // Websocket event.
    RemoteUpdated = 'element:updated', // Websocket event.
    RemoteDeleted = 'element:deleted', // Websocket event.
    RemoteClear = 'elements:clear'
}

export enum FileActions {
    Set = 'set_files'
}

export type WhiteboardSessionState = {
    session: Session | null;
    deleted: boolean;
    templatePage?: Page;
    currentPage?: Page;
    userPage?: Page;
    breakoutPage?: Page;
    pages: Page[];
    presenter: Presenter | null;
    presenterPosition: Position | null;
    notFound: boolean;
    createdElements: ExcalidrawElement[];
    updatedElements: ExcalidrawElement[];
    deletedElementIds: string[];
    remoteCreatedElements: ExcalidrawElement[];
    remoteUpdatedElements: ExcalidrawElement[];
    remoteDeletedElementIds: string[];
    localFiles: BinaryFiles;
    hasNotes: boolean;
};

export type WhiteboardSessionAction =
    | { type: WhiteboardSessionActions.Updated; payload: { session: Session } }
    | { type: WhiteboardSessionActions.Deleted; payload: { session: Session } }
    | { type: WhiteboardSessionActions.Present; payload: { presenter: Presenter | null } }
    | {
          type: WhiteboardSessionActions.PresenterPosition;
          payload: { presenter: Presenter | null };
      }
    | { type: WhiteboardSessionActions.Set; payload: { session: Session } };

export type PageAction =
    | { type: PageActions.Created; payload: { pages: Page[] } }
    | { type: PageActions.Updated; payload: { pages: Page[] } }
    | { type: PageActions.Deleted; payload: { page_ids: string[]; local?: boolean } }
    | { type: PageActions.SetCurrentPage; payload: { page: Page; presenting?: boolean } }
    | { type: PageActions.SetPages; payload: { pages: Page[]; userInfo: UserInfo } }
    | { type: PageActions.AcknowledgeNotFound };

type ElementDeletedAction = { type: ElementActions.Deleted; payload: { element_ids: string[] } };
type RemoteElementDeletedAction = { type: ElementActions.RemoteDeleted; payload: { element_ids: string[] } };

export type ElementAction =
    | { type: ElementActions.Created; payload: { element: ExcalidrawElement } }
    | { type: ElementActions.Updated; payload: { element: ExcalidrawElement } }
    | { type: ElementActions.Clear; payload: { elementIds: string[] } }
    | ElementDeletedAction
    | { type: ElementActions.RemoteCreated; payload: { element: ExcalidrawElement } }
    | { type: ElementActions.RemoteUpdated; payload: { element: ExcalidrawElement } }
    | { type: ElementActions.RemoteClear; payload: { elementIds: string[] } }
    | RemoteElementDeletedAction;

export type FileAction = { type: FileActions.Set; payload: { localFiles: BinaryFiles } };

export type AnyAction = WhiteboardSessionAction | PageAction | ElementAction | FileAction;

const removePages = (pages: Page[], pageIdsToRemove: string[]) =>
    pages.filter((page) => !pageIdsToRemove.includes(page.page_id));

const findElementIndex = (elements: ExcalidrawElement[], element: ExcalidrawElement) =>
    elements.findIndex((e: ExcalidrawElement) => e.id === element.id);

function concatAndDedupeArrays(array1: string[], array2: string[]): string[] {
    const combinedArray = array1.concat(array2);

    const dedupedSet = new Set(combinedArray);
    const dedupedArray = Array.from(dedupedSet);

    return dedupedArray;
}

const combineAndDedupe = (array1, array2) => {
    const combinedArray = [...array1, ...array2];
    const pageIdSet = new Set();

    return combinedArray.filter((item) => {
        if (!pageIdSet.has(item.page_id)) {
            pageIdSet.add(item.page_id);
            return true;
        }
        return false;
    });
};

export const whiteboardSessionReducer = (state: WhiteboardSessionState, action: AnyAction): WhiteboardSessionState => {
    const { type } = action;

    switch (type) {
        case WhiteboardSessionActions.Updated:
            return action.payload.session.session_id === state.session?.session_id
                ? { ...state, session: action.payload.session }
                : state;

        case WhiteboardSessionActions.Deleted:
            return action.payload.session.session_id === state.session?.session_id
                ? { ...state, deleted: true }
                : state;

        case WhiteboardSessionActions.Present:
            return { ...state, presenter: action.payload.presenter };

        case WhiteboardSessionActions.PresenterPosition:
            return {
                ...state,
                presenterPosition: action.payload.presenter?.pointer || null,
                presenter: action.payload.presenter
            };

        case WhiteboardSessionActions.Set:
            return { ...state, session: action.payload.session };

        case PageActions.Created:
            return { ...state, pages: combineAndDedupe(state.pages, action.payload.pages) };

        case PageActions.Updated: {
            const { pages: remoteUpdates } = action.payload;

            const updatedPageIndex = state.pages.findIndex((page) =>
                remoteUpdates.some((updatedPage) => updatedPage.page_id === page.page_id)
            );

            if (updatedPageIndex >= 0) {
                const updatedPages = state.pages.map((page) =>
                    remoteUpdates.some((updatedPage) => updatedPage.page_id === page.page_id)
                        ? remoteUpdates.find((updatedPage) => updatedPage.page_id === page.page_id)
                        : page
                );

                const updatedCurrentPage =
                    state.currentPage?.page_id &&
                    remoteUpdates.some((updatedPage) => updatedPage.page_id === state.currentPage?.page_id)
                        ? remoteUpdates.find((updatedPage) => updatedPage.page_id === state.currentPage?.page_id)
                        : state.currentPage;

                return {
                    ...state,
                    pages: updatedPages as Page[],
                    currentPage: updatedCurrentPage
                };
            }

            return { ...state, pages: [...state.pages, ...action.payload.pages] };
        }

        case PageActions.Deleted: {
            const newPages = removePages(state.pages, action.payload.page_ids);
            const currentPageDeleted = action.payload.page_ids.some((i) => i === state.currentPage?.page_id);

            return {
                ...state,
                pages: newPages,
                currentPage: currentPageDeleted ? newPages[0] : state.currentPage,
                notFound: currentPageDeleted && !action.payload.local
            };
        }

        case PageActions.SetCurrentPage: {
            let { presenter }: any = state;

            if (presenter && action.payload.presenting) {
                presenter = { ...state.presenter, currentPageId: action.payload.page.page_id };
            }

            return {
                ...state,
                presenter,
                currentPage: action.payload.page,
                createdElements: [],
                updatedElements: [],
                deletedElementIds: [],
                remoteCreatedElements: [],
                remoteUpdatedElements: [],
                remoteDeletedElementIds: [],
                localFiles: {}
            };
        }

        case PageActions.SetPages:
            if (!action.payload.pages || action.payload.pages.length < 1) {
                throw new WhiteboardError(`${AT_LEAST_ONE_PAGE_REQUIRED}: At least one page is required.`);
            }

            state.pages = action.payload.pages.sort(({ order: a }, { order: b }) => a - b);

            if (state.currentPage && state.pages.find((p) => p.page_id === state.currentPage!.page_id)) {
                // Current page no longer exists. Reset to first page.
                // eslint-disable-next-line prefer-destructuring
                state.currentPage = state.pages[0];
            }

            state.hasNotes = action.payload.pages.some((p) => p.data?.note && p.data?.note.length > 0);

            return {
                ...state,
                userPage: action.payload.pages.find(
                    (p) => p.created_by === action.payload.userInfo.student_id && p.type === PageTypes.Individual
                ),
                breakoutPage: action.payload.pages.find(
                    (p) =>
                        p.data?.breakout_name === action.payload.userInfo.breakout_name && p.type === PageTypes.Breakout
                ),
                templatePage: action.payload.pages.find((p) => p.type === 'template')
            };

        case PageActions.AcknowledgeNotFound:
            return { ...state, notFound: false };

        case ElementActions.Created:
            // Ensure new element isn't already present/loaded before adding.
            if (findElementIndex(state.createdElements, action.payload.element) === -1) {
                return { ...state, createdElements: [...state.createdElements, action.payload.element] };
            }
            return state;

        case ElementActions.Updated:
            // Ensure updated element is present. If not add it.
            if (findElementIndex(state.updatedElements, action.payload.element) >= 0) {
                return {
                    ...state,
                    updatedElements: state.updatedElements.map((e) =>
                        e.id === action.payload.element.id ? action.payload.element : e
                    )
                };
            }
            // Element isn't present yet. Add it.
            return { ...state, updatedElements: [...state.updatedElements, action.payload.element] };

        case ElementActions.Deleted: {
            const deletedIds = concatAndDedupeArrays(state.deletedElementIds, action.payload.element_ids);
            return {
                ...state,
                deletedElementIds: deletedIds,
                createdElements: state.createdElements.filter((e) => !action.payload.element_ids.includes(e.id)),
                updatedElements: state.updatedElements.filter((e) => !action.payload.element_ids.includes(e.id))
            };
        }

        case ElementActions.Clear:
            return {
                ...state,
                createdElements: state.createdElements.filter((e) => !action.payload.elementIds.includes(e.id)),
                updatedElements: state.updatedElements.filter((e) => !action.payload.elementIds.includes(e.id)),
                deletedElementIds: state.deletedElementIds.filter((id) => !action.payload.elementIds.includes(id))
            };

        case ElementActions.RemoteCreated:
            // Ensure new element isn't already present/loaded before adding.
            if (findElementIndex(state.remoteCreatedElements, action.payload.element) === -1) {
                return { ...state, remoteCreatedElements: [...state.remoteCreatedElements, action.payload.element] };
            }
            return state;

        case ElementActions.RemoteUpdated:
            // Ensure updated element is present. If not add it.
            if (findElementIndex(state.remoteUpdatedElements, action.payload.element) >= 0) {
                return {
                    ...state,
                    remoteUpdatedElements: state.remoteUpdatedElements.map((e) =>
                        e.id === action.payload.element.id ? action.payload.element : e
                    )
                };
            }
            // Element isn't present yet. Add it.
            return { ...state, remoteUpdatedElements: [...state.remoteUpdatedElements, action.payload.element] };

        case ElementActions.RemoteDeleted: {
            const deletedIds = concatAndDedupeArrays(state.remoteDeletedElementIds, action.payload.element_ids);
            return {
                ...state,
                remoteDeletedElementIds: deletedIds,
                remoteUpdatedElements: state.remoteUpdatedElements.filter((e) => !deletedIds.includes(e.id)),
                remoteCreatedElements: state.remoteCreatedElements.filter((e) => !deletedIds.includes(e.id))
            };
        }

        case ElementActions.RemoteClear:
            return {
                ...state,
                remoteCreatedElements: state.remoteCreatedElements.filter(
                    (e) => !action.payload.elementIds.includes(e.id)
                ),
                remoteUpdatedElements: state.remoteUpdatedElements.filter(
                    (e) => !action.payload.elementIds.includes(e.id)
                ),
                remoteDeletedElementIds: state.remoteDeletedElementIds.filter(
                    (id) => !action.payload.elementIds.includes(id)
                )
            };

        // region File actions
        case FileActions.Set:
            return { ...state, localFiles: action.payload.localFiles };

        default:
            throw new WhiteboardError(`${UNKNOWN_ACTION_ERROR}: Unknown action: ${type}.`);
    }
};
